import React, { useState } from 'react';
import { DropResult } from '@hello-pangea/dnd';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import {
  Stack,
  Switch,
  Accordion,
  FormGroup,
  Typography,
  AccordionDetails,
  AccordionSummary,
  FormControlLabel,
} from '@mui/material';

import Tip from '../../../components/Tip';
import useLocales from '../../../hooks/useLocales';
import FormApproversList from './FormApproversList';
import { DraggableApproverProps } from '../../../@types/forms/types';

// ----------------------------------------------------------------------

type FormApproversProps = {
  isDocLogixIntegration: boolean;
  setIsDocLogixIntegration: (isDocLogixIntegration: boolean) => void;
  items: DraggableApproverProps[];
  handleItemChange: (item: DraggableApproverProps) => void;
  onDragEnd: (dropResult: DropResult) => void;
  disabled?: boolean;
  disabledReasonLocale?: string;
};

const FormApprovers: React.FC<FormApproversProps> = ({
  isDocLogixIntegration,
  setIsDocLogixIntegration,
  items,
  handleItemChange,
  onDragEnd,
  disabled = false,
  disabledReasonLocale,
}) => {
  const { t } = useLocales();
  const [isApproversExpanded, setIsApproversExpanded] = useState(false);

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsDocLogixIntegration(event.target.checked);
  };

  return (
    <Accordion
      id="forms-approvers-accordion"
      sx={{ p: 3 }}
      expanded={!disabled && isApproversExpanded}
    >
      <AccordionSummary
        expandIcon={disabled ? null : <ExpandMoreIcon />}
        sx={[
          {
            px: 3,
          },
          isApproversExpanded
            ? {
                py: 3,
              }
            : {
                py: 2,
              },
        ]}
        onClick={() => !disabled && setIsApproversExpanded(!isApproversExpanded)}
        id="A8LquA"
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="h6" sx={{ color: disabled ? 'text.disabled' : 'text.primary' }}>
            {t('forms.approvers')}
          </Typography>

          {disabled && disabledReasonLocale && (
            <Tip longText titleLocale={disabledReasonLocale}>
              <InfoRoundedIcon color="secondary" fontSize="small" />
            </Tip>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ width: '100%', py: 3, pl: 3, pr: 2, pt: 0 }}>
        <FormGroup
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'left',
            alignItems: 'center',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                id="doclogix-integration-switch"
                checked={isDocLogixIntegration}
                onChange={handleSwitchChange}
              />
            }
            label={t('forms.doclogixIntegration')}
            labelPlacement="start"
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'center',
              gap: 1,
            }}
          />
        </FormGroup>

        <FormApproversList
          isDocLogixIntegration={isDocLogixIntegration}
          items={items}
          handleItemChange={handleItemChange}
          onDragEnd={onDragEnd}
        />

        {!isDocLogixIntegration && (
          <Typography variant="caption">{t('formTemplates.checkDragDropReorderList')}</Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default FormApprovers;
